
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface JournalReminder {
  id: string;
  frequency: string;
  time_of_day: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface ReminderFormValues {
  frequency: string;
  time_of_day: string;
  enabled: boolean;
}

export const useJournalReminders = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch reminders
  const { data: reminders = [], isLoading: isLoadingReminders } = useQuery({
    queryKey: ["journal-reminders"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("journal_reminders")
          .select("*")
          .eq('user_id', user.id)
          .order("created_at", { ascending: false });
        
        if (error) {
          toast({
            title: "Erreur",
            description: "Impossible de charger les rappels",
            variant: "destructive",
          });
          throw error;
        }
        return data || [];
      } catch (error) {
        console.error("Error fetching reminders:", error);
        return [];
      }
    },
  });

  // Create reminder
  const createReminder = useMutation({
    mutationFn: async (values: ReminderFormValues) => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { error } = await supabase
          .from("journal_reminders")
          .insert({
            frequency: values.frequency,
            time_of_day: values.time_of_day,
            enabled: values.enabled,
            user_id: user.id,
          });

        if (error) throw error;

        toast({
          title: "Rappel créé",
          description: "Votre rappel a été créé avec succès",
        });
        
        return true;
      } catch (error) {
        console.error("Error creating reminder:", error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de créer le rappel",
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-reminders"] });
    },
  });

  // Update reminder
  const updateReminder = useMutation({
    mutationFn: async ({ id, ...values }: JournalReminder) => {
      setIsLoading(true);
      try {
        const { error } = await supabase
          .from("journal_reminders")
          .update({
            frequency: values.frequency,
            time_of_day: values.time_of_day,
            enabled: values.enabled,
          })
          .eq('id', id);

        if (error) throw error;

        toast({
          title: "Rappel mis à jour",
          description: "Votre rappel a été mis à jour avec succès",
        });
        
        return true;
      } catch (error) {
        console.error("Error updating reminder:", error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de mettre à jour le rappel",
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-reminders"] });
    },
  });

  // Delete reminder
  const deleteReminder = useMutation({
    mutationFn: async (id: string) => {
      setIsLoading(true);
      try {
        const { error } = await supabase
          .from("journal_reminders")
          .delete()
          .eq('id', id);

        if (error) throw error;

        toast({
          title: "Rappel supprimé",
          description: "Votre rappel a été supprimé avec succès",
        });
        
        return true;
      } catch (error) {
        console.error("Error deleting reminder:", error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de supprimer le rappel",
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-reminders"] });
    },
  });

  // Listen for real-time changes
  useEffect(() => {
    const channel = supabase
      .channel('reminder-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'journal_reminders'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["journal-reminders"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    reminders,
    isLoading: isLoading || isLoadingReminders,
    createReminder: createReminder.mutate,
    updateReminder: updateReminder.mutate,
    deleteReminder: deleteReminder.mutate,
  };
};
